import * as tslib_1 from "tslib";
import { EventDispatcher } from "../../events/EventDispatcher";
/// Dispatched when the display list is about to be rendered.
/*[Event(name="render", type="starling.events.Event")]*/
/// Dispatched when a fatal error is encountered.
/*[Event(name="fatalError", tFype="starling.events.Event")]*/
/// Dispatched when the root class has been created.
/*[Event(name="rootCreated", type="starling.events.Event")]*/
/// Dispatched when a new render context is created.
/*[Event(name="context3DCreate", type="starling.events.Event")]*/
/**
 * The Starling class represents the core of the Starling framework.
 * <p>The Starling framework makes it possible to create 2D applications and games that make
 * use of the Stage3D architecture introduced in Flash Player 11. It implements a display tree
 * system that is very similar to that of conventional Flash, while leveraging modern GPUs
 * to speed up rendering.</p><p>The Starling class represents the link between the conventional Flash display tree and
 * the Starling display tree. To create a Starling-powered application, you have to create
 * an instance of the Starling class:</p><pre>var starling:Starling = new Starling(Game, stage);</pre><p>The first parameter has to be a Starling display object class, e.g. a subclass of
 * <codeph>starling.display.Sprite</codeph>. In the sample above, the class "Game" is the
 * application root. An instance of "Game" will be created as soon as Starling is initialized.
 * The second parameter is the conventional (Flash) stage object. Per default, Starling will
 * display its contents directly below the stage.</p><p>It is recommended to store the Starling instance as a member variable, to make sure
 * that the Garbage Collector does not destroy it. After creating the Starling object, you
 * have to start it up like this:</p><pre>starling.start();</pre><p>It will now render the contents of the "Game" class in the frame rate that is set up for
 * the application (as defined in the Flash stage).</p><b>Context3D Profiles</b><p>Stage3D supports different rendering profiles, and Starling works with all of them. The
 * last parameter of the Starling constructor allows you to choose which profile you want.
 * The following profiles are available:</p><ul><li>BASELINE_CONSTRAINED: provides the broadest hardware reach. If you develop for the
 * browser, this is the profile you should test with.</li><li>BASELINE: recommend for any mobile application, as it allows Starling to use a more
 * memory efficient texture type (RectangleTextures). It also supports more complex
 * AGAL code.</li><li>BASELINE_EXTENDED: adds support for textures up to 4096x4096 pixels. This is
 * especially useful on mobile devices with very high resolutions.</li></ul><p>The recommendation is to deploy your app with the profile "auto" (which makes Starling
 * pick the best available of those three), but test it in all available profiles.</p><b>Accessing the Starling object</b><p>From within your application, you can access the current Starling object anytime
 * through the static method <codeph>Starling.current</codeph>. It will return the active Starling
 * instance (most applications will only have one Starling object, anyway).</p><b>Viewport</b><p>The area the Starling content is rendered into is, per default, the complete size of the
 * stage. You can, however, use the "viewPort" property to change it. This can be  useful
 * when you want to render only into a part of the screen, or if the player size changes. For
 * the latter, you can listen to the RESIZE-event dispatched by the Starling
 * stage.</p><b>Native overlay</b><p>Sometimes you will want to display native Flash content on top of Starling. That's what the
 * <codeph>nativeOverlay</codeph> property is for. It returns a Flash Sprite lying directly
 * on top of the Starling content. You can add conventional Flash objects to that overlay.</p><p>Beware, though, that conventional Flash content on top of 3D content can lead to
 * performance penalties on some (mobile) platforms. For that reason, always remove all child
 * objects from the overlay when you don't need them any longer. Starling will remove the
 * overlay from the display list when it's empty.</p><b>Multitouch</b><p>Starling supports multitouch input on devices that provide it. During development,
 * where most of us are working with a conventional mouse and keyboard, Starling can simulate
 * multitouch events with the help of the "Shift" and "Ctrl" (Mac: "Cmd") keys. Activate
 * this feature by enabling the <codeph>simulateMultitouch</codeph> property.</p><b>Handling a lost render context</b><p>On some operating systems and under certain conditions (e.g. returning from system
 * sleep), Starling's stage3D render context may be lost. Starling can recover from a lost
 * context if the class property "handleLostContext" is set to "true". Keep in mind, however,
 * that this comes at the price of increased memory consumption; Starling will cache textures
 * in RAM to be able to restore them when the context is lost. (Except if you use the
 * 'AssetManager' for your textures. It is smart enough to recreate a texture directly
 * from its origin.)</p><p>In case you want to react to a context loss, Starling dispatches an event with
 * the type "Event.CONTEXT3D_CREATE" when the context is restored. You can recreate any
 * invalid resources in a corresponding event listener.</p><b>Sharing a 3D Context</b><p>Per default, Starling handles the Stage3D context itself. If you want to combine
 * Starling with another Stage3D engine, however, this may not be what you want. In this case,
 * you can make use of the <codeph>shareContext</codeph> property:</p><ol><li>Manually create and configure a context3D object that both frameworks can work with
 * (through <codeph>stage3D.requestContext3D</codeph> and
 * <codeph>context.configureBackBuffer</codeph>).</li><li>Initialize Starling with the stage3D instance that contains that configured context.
 * This will automatically enable <codeph>shareContext</codeph>.</li><li>Call <codeph>start()</codeph> on your Starling instance (as usual). This will make
 * Starling queue input events (keyboard/mouse/touch).</li><li>Create a game loop (e.g. using the native <codeph>ENTER_FRAME</codeph> event) and let it
 * call Starling's <codeph>nextFrame</codeph> as well as the equivalent method of the other
 * Stage3D engine. Surround those calls with <codeph>context.clear()</codeph> and
 * <codeph>context.present()</codeph>.</li></ol><p>The Starling wiki contains a <xref href="http://goo.gl/BsXzw">tutorial</xref> with more
 * information about this topic.</p>
 */
var Starling = (function (_super) {
    tslib_1.__extends(Starling, _super);
    /**
     * Creates a new Starling instance.
     * @param	rootClass	A subclass of 'starling.display.DisplayObject'. It will be created
     *   as soon as initialization is finished and will become the first child
     *   of the Starling stage. Pass null if you don't want to
     *   create a root object right away. (You can use the
     *   rootClass property later to make that happen.)
     * @param	stage	The Flash (2D) stage.
     * @param	viewPort	A rectangle describing the area into which the content will be
     *   rendered. Default: stage size
     * @param	stage3D	The Stage3D object into which the content will be rendered. If it
     *   already contains a context, sharedContext will be set
     *   to true. Default: the first available Stage3D.
     * @param	renderMode	The Context3D render mode that should be requested.
     *   Use this parameter if you want to force "software" rendering.
     * @param	profile	The Context3D profile that should be requested.
     *   If you pass a profile String, this profile is enforced.Pass an Array of profiles to make Starling pick the first
     *   one that works (starting with the first array element).Pass the String "auto" to make Starling pick the best available
     *   profile automatically.
     */
    function Starling(rootClass, stage, viewPort, stage3D, renderMode, profile) {
        if (viewPort === void 0) { viewPort = null; }
        if (stage3D === void 0) { stage3D = null; }
        if (renderMode === void 0) { renderMode = "auto"; }
        if (profile === void 0) { profile = "baselineConstrained"; }
        //todo: any is Stage3D
        return _super.call(this) || this;
    }
    Object.defineProperty(Starling, "all", {
        /// All Starling instances. CAUTION: not a copy, but the actual object! Do not modify!
        get: function () {
            console.log("all not implemented yet in starling/Starling");
            return [];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "antiAliasing", {
        /// The antialiasing level. 0 - no antialasing, 16 - maximum antialiasing.
        get: function () {
            console.log("antiAliasing not implemented yet in starling/Starling");
            return 0;
        },
        set: function (value) {
            console.log("antiAliasing not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "backBufferHeight", {
        /**
         * Returns the current height of the back buffer. In most cases, this value is in pixels;
         * however, if the app is running on an HiDPI display with an activated
         * 'supportHighResolutions' setting, you have to multiply with 'backBufferPixelsPerPoint'
         * for the actual pixel count.
         */
        get: function () {
            console.log("backBufferHeight not implemented yet in starling/Starling");
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "backBufferPixelsPerPoint", {
        /**
         * The number of pixel per point returned by the 'backBufferWidth/Height' properties.
         * Except for desktop HiDPI displays with an activated 'supportHighResolutions' setting,
         * this will always return '1'.
         */
        get: function () {
            console.log("backBufferPixelsPerPoint not implemented yet in starling/Starling");
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "backBufferWidth", {
        /**
         * Returns the current width of the back buffer. In most cases, this value is in pixels;
         * however, if the app is running on an HiDPI display with an activated
         * 'supportHighResolutions' setting, you have to multiply with 'backBufferPixelsPerPoint'
         * for the actual pixel count.
         */
        get: function () {
            console.log("backBufferWidth not implemented yet in starling/Starling");
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "contentScaleFactor", {
        /**
         * The ratio between viewPort width and stage width. Useful for choosing a different
         * set of textures depending on the display resolution.
         */
        get: function () {
            console.log("contentScaleFactor not implemented yet in starling/Starling");
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling, "contentScaleFactor", {
        /**
         * The ratio between viewPort width and stage width. Useful for choosing a different
         * set of textures depending on the display resolution.
         */
        get: function () {
            console.log("contentScaleFactor not implemented yet in starling/Starling");
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "context", {
        /// The render context of this instance.
        get: function () {
            console.log("context not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling, "context", {
        /// The render context of this instance.
        get: function () {
            console.log("context not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "contextData", {
        //todo any is Objects.Dictionary
        /**
         * A dictionary that can be used to save custom data related to the current context.
         * If you need to share data that is bound to a specific stage3D instance
         * (e.g. textures), use this dictionary instead of creating a static class variable.
         * The Dictionary is actually bound to the stage3D instance, thus it survives a
         * context loss.
         */
        get: function () {
            //todo any is Objects.Dictionary
            console.log("contextData not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "contextValid", {
        /**
         * Indicates if the Context3D object is currently valid (i.e. it hasn't been lost or
         * disposed).
         */
        get: function () {
            console.log("contextValid not implemented yet in starling/Starling");
            return false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling, "current", {
        /// The currently active Starling instance.
        get: function () {
            console.log("current not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "enableErrorChecking", {
        /**
         * Indicates if Stage3D render methods will report errors. Activate only when needed,
         * as this has a negative impact on performance.
         */
        get: function () {
            console.log("enableErrorChecking not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("enableErrorChecking not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling, "handleLostContext", {
        /**
         * Indicates if Starling should automatically recover from a lost device context.
         * On some systems, an upcoming screensaver or entering sleep mode may
         * invalidate the render context. This setting indicates if Starling should recover from
         * such incidents.
         * Beware: if used carelessly, this property may have a huge impact on memory
         * consumption. That's because, by default, it will make Starling keep a copy of each
         * texture in memory.However, this downside can be avoided by using the "AssetManager" to load textures.
         * The AssetManager is smart enough to restore them directly from their sources. You can
         * also do this by setting up "root.onRestore" on your manually loaded textures.A context loss can happen on almost every platform. It's very common on Windows
         * and Android, but rare on OS X and iOS (e.g. it may occur when opening up the camera
         * roll). It's recommended to always enable this property, while using the AssetManager
         * for texture loading.
         */
        get: function () {
            console.log("handleLostContext not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("handleLostContext not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "isStarted", {
        /// Indicates if this Starling instance is started.
        get: function () {
            console.log("isStarted not implemented yet in starling/Starling");
            return false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "juggler", {
        /// The default juggler of this instance. Will be advanced once per frame.
        get: function () {
            console.log("juggler not implemented yet in starling/Starling");
            //todo any is Jugglerg.animation.Juggler
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling, "juggler", {
        /// The default juggler of this instance. Will be advanced once per frame.
        get: function () {
            console.log("juggler not implemented yet in starling/Starling");
            //todo any is Jugglerg.animation.Juggler
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling, "multitouchEnabled", {
        /// Indicates if multitouch input should be supported.
        get: function () {
            console.log("multitouchEnabled not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("multitouchEnabled not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "nativeOverlay", {
        /**
         * A Flash Sprite placed directly on top of the Starling content. Use it to display native
         * Flash components.
         */
        get: function () {
            console.log("nativeOverlay not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "nativeStage", {
        /// The Flash (2D) stage object Starling renders beneath.
        get: function () {
            console.log("nativeStage not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "profile", {
        /**
         * The Context3D profile used for rendering. Beware that if you are using a shared
         * context in AIR 3.9 / Flash Player 11 or below, this is simply what you passed to
         * the Starling constructor.
         */
        get: function () {
            console.log("profile not implemented yet in starling/Starling");
            return "";
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "root", {
        /**
         * The instance of the root class provided in the constructor. Available as soon as
         * the event 'ROOT_CREATED' has been dispatched.
         */
        get: function () {
            console.log("root not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "rootClass", {
        /**
         * The class that will be instantiated by Starling as the 'root' display object.
         * Must be a subclass of 'starling.display.DisplayObject'.
         * If you passed null as first parameter to the Starling constructor,
         * you can use this property to set the root class at a later time. As soon as the class
         * is instantiated, Starling will dispatch a ROOT_CREATED event.Beware: you cannot change the root class once the root object has been
         * instantiated.
         */
        get: function () {
            console.log("rootClass not implemented yet in starling/Starling");
            return null;
        },
        set: function (value) {
            console.log("rootClass not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "shareContext", {
        /**
         * Indicates if the Context3D render calls are managed externally to Starling,
         * to allow other frameworks to share the Stage3D instance.
         */
        get: function () {
            console.log("shareContext not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("shareContext not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "showStats", {
        /**
         * Indicates if a small statistics box (with FPS, memory usage and draw count) is
         * displayed.
         * Beware that the memory usage should be taken with a grain of salt. The value is
         * determined via System.totalMemory and does not take texture memory
         * into account. It is recommended to use Adobe Scout for reliable and comprehensive
         * memory analysis.
         */
        get: function () {
            console.log("showStats not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("showStats not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "simulateMultitouch", {
        /// Indicates if multitouch simulation with "Shift" and "Ctrl"/"Cmd"-keys is enabled.
        get: function () {
            console.log("simulateMultitouch not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("simulateMultitouch not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "stage", {
        /// The Starling stage object, which is the root of the display tree that is rendered.
        get: function () {
            console.log("stage not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "stage3D", {
        /// The Flash Stage3D object Starling renders into.
        get: function () {
            //todo:any is Stage3D
            console.log("stage3D not implemented yet in starling/Starling");
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "supportHighResolutions", {
        /**
         * Indicates that if the device supports HiDPI screens Starling will attempt to allocate
         * a larger back buffer than indicated via the viewPort size. Note that this is used
         * on Desktop only; mobile AIR apps still use the "requestedDisplayResolution" parameter
         * the application descriptor XML.
         */
        get: function () {
            console.log("supportHighResolutions not implemented yet in starling/Starling");
            return false;
        },
        set: function (value) {
            console.log("supportHighResolutions not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "touchProcessor", {
        /**
         * The TouchProcessor is passed all mouse and touch input and is responsible for
         * dispatching TouchEvents to the Starling display tree. If you want to handle these
         * types of input manually, pass your own custom subclass to this property.
         */
        get: function () {
            //todo: any is TouchProcessor
            console.log("touchProcessor not implemented yet in starling/Starling");
            return null;
        },
        set: function (value) {
            //todo: TouchProcessor
            console.log("touchProcessor not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Starling.prototype, "viewPort", {
        /// The viewport into which Starling contents will be rendered.
        get: function () {
            console.log("viewPort not implemented yet in starling/Starling");
            return null;
        },
        set: function (value) {
            console.log("viewPort not implemented yet in starling/Starling");
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Dispatches ENTER_FRAME events on the display list, advances the Juggler
     * and processes touches.
     */
    Starling.prototype.advanceTime = function (passedTime) {
        console.log("advanceTime not implemented yet in starling/Starling");
    };
    /// Deletes the vertex- and fragment-programs of a certain name.
    Starling.prototype.deleteProgram = function (name) {
        console.log("deleteProgram not implemented yet in starling/Starling");
    };
    /**
     * Disposes all children of the stage and the render context; removes all registered
     * event listeners.
     */
    Starling.prototype.dispose = function () {
        console.log("dispose not implemented yet in starling/Starling");
    };
    /// Returns the vertex- and fragment-programs registered under a certain name.
    Starling.prototype.getProgram = function (name) {
        //todo: any is Program3D
        console.log("getProgram not implemented yet in starling/Starling");
        return null;
    };
    /// Indicates if a set of vertex- and fragment-programs is registered under a certain name.
    Starling.prototype.hasProgram = function (name) {
        console.log("hasProgram not implemented yet in starling/Starling");
        return false;
    };
    /// Make this Starling instance the current one.
    Starling.prototype.makeCurrent = function () {
        console.log("makeCurrent not implemented yet in starling/Starling");
    };
    /**
     * Calls advanceTime() (with the time that has passed since the last frame)
     * and render().
     */
    Starling.prototype.nextFrame = function () {
        console.log("nextFrame not implemented yet in starling/Starling");
    };
    /**
     * Registers a compiled shader-program under a certain name.
     * If the name was already used, the previous program is overwritten.
     */
    Starling.prototype.registerProgram = function (name, vertexShader, fragmentShader) {
        //todo: any is Program3D
        console.log("registerProgram not implemented yet in starling/Starling");
        return null;
    };
    /**
     * Compiles a shader-program and registers it under a certain name.
     * If the name was already used, the previous program is overwritten.
     */
    Starling.prototype.registerProgramFromSource = function (name, vertexShader, fragmentShader) {
        //todo: any is Program3D
        console.log("registerProgramFromSource not implemented yet in starling/Starling");
        return null;
    };
    /**
     * Renders the complete display list. Before rendering, the context is cleared; afterwards,
     * it is presented (to avoid this, enable shareContext).
     * This method also dispatches an Event.RENDER-event on the Starling
     * instance. That's the last opportunity to make changes before the display list is
     * rendered.
     */
    Starling.prototype.render = function () {
        console.log("render not implemented yet in starling/Starling");
    };
    /// Displays the statistics box at a certain position.
    Starling.prototype.showStatsAt = function (hAlign, vAlign, scale) {
        if (hAlign === void 0) { hAlign = "left"; }
        if (vAlign === void 0) { vAlign = "top"; }
        if (scale === void 0) { scale = 1; }
        console.log("showStatsAt not implemented yet in starling/Starling");
    };
    /**
     * As soon as Starling is started, it will queue input events (keyboard/mouse/touch);
     * furthermore, the method nextFrame will be called once per Flash Player
     * frame. (Except when shareContext is enabled: in that case, you have to
     * call that method manually.)
     */
    Starling.prototype.start = function () {
        console.log("start not implemented yet in starling/Starling");
    };
    /**
     * Stops all logic and input processing, effectively freezing the app in its current state.
     * Per default, rendering will continue: that's because the classic display list
     * is only updated when stage3D is. (If Starling stopped rendering, conventional Flash
     * contents would freeze, as well.)
     *
     *   However, if you don't need classic Flash contents, you can stop rendering, too.
     * On some mobile systems (e.g. iOS), you are even required to do so if you have
     * activated background code execution.
     */
    Starling.prototype.stop = function (suspendRendering) {
        if (suspendRendering === void 0) { suspendRendering = false; }
        console.log("stop not implemented yet in starling/Starling");
    };
    /**
     * Stops Starling right away and displays an error message on the native overlay.
     * This method will also cause Starling to dispatch a FATAL_ERROR event.
     */
    Starling.prototype.stopWithFatalError = function (message) {
        console.log("stopWithFatalError not implemented yet in starling/Starling");
    };
    return Starling;
}(EventDispatcher));
export { Starling };
/// The version of the Starling framework.
Starling.VERSION = "1.7";
